import type {
  TError,
  TReviewFilterMode,
  TReviewQueueDateField,
  TReviewQueueFacetsResponse,
  TReviewQueueResponse,
  TReviewSubmissionStatus,
  TReviewTimeState,
} from '@onesource/schemas';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import axios from '../../axios';
import { ENTITY } from '../entity';

export const getReviewQueryKey = (spaceId: string) => [ENTITY, 'review', spaceId];

/** The filters both review lists share, as the page holds them; lists go to the API comma-joined. */
export interface TReviewFilterParams {
  search?: string;
  moduleIds: string[];
  moduleMode: TReviewFilterMode;
  formIds: string[];
  formMode: TReviewFilterMode;
  teacherIds: string[];
  teacherMode: TReviewFilterMode;
  /** ISO date-times; either end may be open. */
  dateFrom?: string;
  dateTo?: string;
}

export interface TReviewQueueParams extends TReviewFilterParams {
  status: TReviewSubmissionStatus | 'all';
  reviewTime: TReviewTimeState[];
  reviewTimeMode: TReviewFilterMode;
  dateField: TReviewQueueDateField;
  page?: number;
  perPage?: number;
}

/** Each list filter and the include / exclude mode that goes with it. */
const LIST_MODES: Record<string, string> = {
  moduleIds: 'moduleMode',
  formIds: 'formMode',
  teacherIds: 'teacherMode',
  reviewTime: 'reviewTimeMode',
  progress: 'progressMode',
  due: 'dueMode',
};

/**
 * Only the filters in use go on the URL, so the defaults keep the query short and
 * cache keys stable: lists comma-joined, a mode only with its list, the date field
 * only with a date.
 */
export const toReviewSearchParams = (params: object): Record<string, string | number> => {
  const out: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      if (value.length > 0) {
        out[key] = value.join(',');
      }
    } else if ((typeof value === 'string' && value !== '') || typeof value === 'number') {
      out[key] = value;
    }
  }
  for (const [list, mode] of Object.entries(LIST_MODES)) {
    if (out[list] === undefined) {
      delete out[mode];
    }
  }
  if (out['dateFrom'] === undefined && out['dateTo'] === undefined) {
    delete out['dateField'];
  }
  return out;
};

const toSearchParams = (params: TReviewQueueParams) => toReviewSearchParams(params);

/** The requester's review queue in a Teacher Review sub-space. */
export const useGetReviewQueue = (spaceId: string, params: TReviewQueueParams) =>
  useQuery<TReviewQueueResponse, TError>({
    queryKey: [...getReviewQueryKey(spaceId), 'queue', toSearchParams(params)],
    queryFn: async () => {
      const res = await axios.get<TReviewQueueResponse>(`/community-hub/spaces/${spaceId}/review/submissions`, {
        params: toSearchParams(params),
      });
      return res.data;
    },
    enabled: !!spaceId,
    placeholderData: keepPreviousData,
  });

/**
 * The values each queue filter offers, each counted under the other filters. Only
 * fetched while a filter panel is open.
 */
export const useGetReviewFacets = (spaceId: string, params: TReviewQueueParams, enabled: boolean) => {
  const { page: _page, perPage: _perPage, ...filters } = params;
  return useQuery<TReviewQueueFacetsResponse, TError>({
    queryKey: [...getReviewQueryKey(spaceId), 'facets', toSearchParams(filters)],
    queryFn: async () => {
      const res = await axios.get<TReviewQueueFacetsResponse>(`/community-hub/spaces/${spaceId}/review/facets`, {
        params: toSearchParams(filters),
      });
      return res.data;
    },
    enabled: !!spaceId && enabled,
    placeholderData: keepPreviousData,
  });
};
